import { clamp, Easing } from '@/lib/anim/easing';

import { CONTACTS, T, TIERS, type StoryContact } from './storyboard';

// Ch. 03 - grade: a mock pipeline where each row lands in turn and its tier
// colour resolves a beat after it arrives.
export function Scene3Grade({ time }: { time: number }) {
  const [start, end] = T.grade;
  if (time < start || time > end) return null;
  const localTime = time - start;

  const titleIn = Easing.easeOutCubic(clamp(localTime / 0.7, 0, 1));
  const titleOut = clamp((localTime - 3) / 0.6, 0, 1);
  const titleOpacity = titleIn * (1 - titleOut);

  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: 120,
          top: 230,
          width: 540,
          opacity: titleOpacity,
          transform: `translateY(${(1 - titleIn) * 18}px)`,
          zIndex: 2,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
          <span style={{ width: 32, height: 1, background: 'var(--assay-ore)' }} />
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: '0.26em',
              textTransform: 'uppercase',
              color: 'var(--assay-ore)',
            }}
          >
            Ch. 03 - Grade
          </span>
        </div>
        <h2
          style={{
            fontFamily: '"Cormorant Garamond", "Georgia", serif',
            fontWeight: 300,
            fontSize: 64,
            lineHeight: 1.05,
            color: 'var(--assay-text-primary)',
            letterSpacing: '-0.02em',
            margin: '0 0 24px',
          }}
        >
          Sort your network by <em style={{ color: 'var(--assay-ore-light)' }}>trust</em>.
        </h2>
        <p
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 15,
            lineHeight: 1.7,
            color: 'var(--assay-text-body)',
            margin: 0,
            maxWidth: 460,
          }}
        >
          Honest tiers set how often to stay in touch. Four grades, applied across every contact,
          each with its own rhythm for reaching out.
        </p>
      </div>

      <div
        style={{
          position: 'absolute',
          right: 100,
          top: 130,
          width: 640,
          background: 'var(--assay-surface)',
          border: '1px solid var(--assay-ore-border)',
          borderRadius: 2,
          boxShadow: '0 12px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(196,152,58,0.08)',
          opacity: titleOpacity,
          transform: `translateY(${(1 - titleIn) * 24}px) scale(${0.985 + 0.015 * titleIn})`,
          zIndex: 2,
        }}
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--assay-ore-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'rgba(196,152,58,0.025)',
          }}
        >
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--assay-text-label)',
            }}
          >
            Pipeline · 8 of 312
          </span>
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              letterSpacing: '0.14em',
              color: 'var(--assay-ore-light)',
            }}
          >
            Sorted: highest trust first
          </span>
        </div>
        {CONTACTS.map((c, i) => (
          <ContactRow key={c.name} contact={c} index={i} localTime={localTime} />
        ))}
      </div>
    </>
  );
}

function ContactRow({
  contact,
  index,
  localTime,
}: {
  contact: StoryContact;
  index: number;
  localTime: number;
}) {
  const tStart = 0.5 + index * 0.18;
  const tFadeOut = 3;
  const inT = Easing.easeOutCubic(clamp((localTime - tStart) / 0.55, 0, 1));
  const outT = clamp((localTime - tFadeOut) / 0.7, 0, 1);
  const opacity = inT * (1 - outT);
  const tx = (1 - inT) * 32;

  const colorT = clamp((localTime - tStart - 0.25) / 0.35, 0, 1);
  const tier = TIERS[contact.tier];
  const dotColor = colorT >= 1 ? tier.color : `rgba(154,132,112,${0.6 + 0.4 * colorT})`;

  return (
    <div
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '38px 1fr auto',
        alignItems: 'center',
        gap: 18,
        padding: '10px 18px 10px 20px',
        borderBottom: '1px solid rgba(196,152,58,0.08)',
        opacity,
        transform: `translateX(${tx}px)`,
        background: index % 2 === 0 ? 'rgba(255,255,255,0.005)' : 'transparent',
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 2,
          background: dotColor,
          opacity: colorT,
          boxShadow: `0 0 10px ${tier.color}`,
        }}
      />
      <StrataMini fill={tier.fill} color={tier.color} height={36} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 15,
            color: 'var(--assay-text-primary)',
            letterSpacing: '0.01em',
          }}
        >
          {contact.name}
        </span>
        <span
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 11,
            color: 'var(--assay-text-label)',
            letterSpacing: '0.02em',
          }}
        >
          {contact.role}
        </span>
      </div>
      <span
        style={{
          fontFamily: '"DM Mono", monospace',
          fontSize: 9.5,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: tier.color,
          opacity: colorT,
          padding: '4px 9px',
          border: `1px solid ${tier.color}40`,
          borderRadius: 2,
          background: `${tier.color}10`,
        }}
      >
        {tier.short}
      </span>
    </div>
  );
}

function StrataMini({
  fill,
  color,
  height = 38,
}: {
  fill: number;
  color: string;
  height?: number;
}) {
  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 8,
        height,
        background: '#0c0906',
        border: '1px solid rgba(196,152,58,0.14)',
        borderRadius: 1,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: `${fill}%`,
          background: `linear-gradient(to top, #14110a 0%, ${color} 100%)`,
        }}
      />
      {fill >= 65 && (
        <span
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${fill}%`,
            height: 1,
            background: color,
            boxShadow: `0 0 4px ${color}`,
          }}
        />
      )}
    </span>
  );
}
